import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { activityApi, reminderApi, type RepeatConfig, type Reminder } from '@/services/api'
import Button from '@/components/ui/Button'
import ReminderPicker from '@/components/ui/ReminderPicker'
import CustomReminderModal from '@/components/ui/CustomReminderModal'
import ReminderModal from '@/components/ui/ReminderModal'
import styles from './ActivityComplete.module.css'
import Icon from '@/components/ui/Icon'

// Must match backend ActivityLog.FEEDBACK_CHOICES
type FeedbackType = 'helped' | 'didnt_help' | 'skipped'

export default function ActivityComplete() {
  const { activityId } = useParams<{ activityId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { logId, activityTitle } = (location.state as { logId?: number; activityTitle?: string }) || {}

  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [showReminderPicker, setShowReminderPicker] = useState(false)
  const [showCustomReminder, setShowCustomReminder] = useState(false)
  const [reminderMode, setReminderMode] = useState<'once' | 'routine'>('once')
  const [reminderSaved, setReminderSaved] = useState(false)
  const [lastSavedReminder, setLastSavedReminder] = useState<Reminder | null>(null)
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null)
  const [reminders, setReminders] = useState<Reminder[]>([])

  // Fetch existing reminders for this activity
  useEffect(() => {
    if (!activityId) return

    reminderApi.getReminders({ activity_id: activityId })
      .then((res) => {
        const pendingReminders = (res.data || []).filter(
          (r: Reminder) => r.status === 'pending'
        )
        setReminders(pendingReminders)
      })
      .catch(() => {
        // Ignore errors fetching reminders
      })
  }, [activityId])

  const handleFeedback = async (feedback: FeedbackType) => {
    setSelectedFeedback(feedback)

    if (logId) {
      setSubmitting(true)
      try {
        await activityApi.updateLog(String(logId), {
          feedback,
          completed: true,
        })
      } catch {
        // Continue even if update fails
      } finally {
        setSubmitting(false)
      }
    }
  }

  const handleDone = () => {
    navigate('/')
  }

  const handleReminderSave = async (scheduledAt: Date, repeatConfig?: RepeatConfig) => {
    if (!activityId) return

    try {
      // Use provided repeatConfig first, otherwise fall back to reminderMode default
      const config = repeatConfig || (reminderMode === 'routine'
        ? { type: 'daily' as const, end_date: getDefaultEndDate() }
        : { type: 'once' as const })

      const response = await reminderApi.createReminder({
        activity_id: activityId,
        activity_title: activityTitle,
        scheduled_at: scheduledAt.toISOString(),
        repeat_config: config,
      })
      setLastSavedReminder(response.data)
      setReminderSaved(true)
      setShowReminderPicker(false)
      setShowCustomReminder(false)
      setShowReminderModal(false)
      // Refresh reminders list
      const remindersRes = await reminderApi.getReminders({ activity_id: activityId })
      const pendingReminders = (remindersRes.data || []).filter(
        (r: Reminder) => r.status === 'pending'
      )
      setReminders(pendingReminders)
    } catch {
      alert('Failed to save reminder. Please try again.')
    }
  }

  const handleReminderUpdate = async (reminderId: string, scheduledAt: Date, repeatConfig?: RepeatConfig) => {
    try {
      const response = await reminderApi.updateReminder(reminderId, {
        scheduled_at: scheduledAt.toISOString(),
        repeat_config: repeatConfig,
      })
      setLastSavedReminder(response.data)
      setEditingReminder(null)
      setShowCustomReminder(false)
      setReminderSaved(true)
      // Refresh reminders list
      setReminders(reminders.map(r => r.id === reminderId ? response.data : r))
    } catch {
      alert('Failed to update reminder. Please try again.')
    }
  }

  const handleReminderDelete = async (reminderId: string) => {
    try {
      await reminderApi.deleteReminder(reminderId)
      setReminders(reminders.filter(r => r.id !== reminderId))
    } catch {
      alert('Failed to delete reminder. Please try again.')
    }
  }

  const handleDoThisAgain = () => {
    setReminderMode('once')
    // Show modal if reminders exist, otherwise go straight to picker
    if (reminders.length > 0) {
      setShowReminderModal(true)
    } else {
      setShowReminderPicker(true)
    }
  }

  const handleMakeRoutine = () => {
    setReminderMode('routine')
    // Show modal if reminders exist, otherwise go straight to picker
    if (reminders.length > 0) {
      setShowReminderModal(true)
    } else {
      setShowReminderPicker(true)
    }
  }

  // Helper to get default end date (1 month from now)
  const getDefaultEndDate = () => {
    const date = new Date()
    date.setMonth(date.getMonth() + 1)
    return date.toISOString().split('T')[0]
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <span className={styles.icon}><Icon name="party-popper" size={22} /></span>
        <h1 className={styles.title}>Great job!</h1>
        <p className={styles.message}>
          You completed the activity. How did it go?
        </p>

        <div className={styles.feedback}>
          <button
            className={`${styles.feedbackButton} ${selectedFeedback === 'helped' ? styles.feedbackSelected : ''}`}
            onClick={() => handleFeedback('helped')}
            disabled={submitting}
          >
             Helped
          </button>
          <button
            className={`${styles.feedbackButton} ${selectedFeedback === 'skipped' ? styles.feedbackSelected : ''}`}
            onClick={() => handleFeedback('skipped')}
            disabled={submitting}
          >
             Skipped
          </button>
          <button
            className={`${styles.feedbackButton} ${selectedFeedback === 'didnt_help' ? styles.feedbackSelected : ''}`}
            onClick={() => handleFeedback('didnt_help')}
            disabled={submitting}
          >
             Didn't help
          </button>
        </div>

        {/* Reminder prompt section */}
        {selectedFeedback && !reminderSaved && (
          <div className={styles.reminderSection}>
            <p className={styles.reminderPrompt}>Want to try this again?</p>
            <div className={styles.reminderButtons}>
              <button
                className={styles.reminderOption}
                onClick={handleDoThisAgain}
              >
                 Do this again
              </button>
              <button
                className={styles.reminderOption}
                onClick={handleMakeRoutine}
              >
                 Make it a routine
              </button>
            </div>
          </div>
        )}

        {reminderSaved && lastSavedReminder && (
          <div className={styles.reminderSuccess}>
            <div className={styles.reminderSuccessContent}>
              <span className={styles.reminderSuccessIcon}><Icon name="bell" size={22} /></span>
              <div className={styles.reminderSuccessInfo}>
                <span className={styles.reminderSuccessTitle}>Reminder set!</span>
                <span className={styles.reminderSuccessTime}>
                  {new Date(lastSavedReminder.scheduled_at).toLocaleDateString('en-IN', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}{' '}
                  at{' '}
                  {new Date(lastSavedReminder.scheduled_at).toLocaleTimeString('en-IN', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                  {lastSavedReminder.repeat_config?.type !== 'once' && (
                    <span className={styles.repeatBadge}>
                      {lastSavedReminder.repeat_config?.type === 'daily' && ' • Daily'}
                      {lastSavedReminder.repeat_config?.type === 'weekly' && ' • Weekly'}
                      {lastSavedReminder.repeat_config?.type === 'custom' && ' • Custom'}
                    </span>
                  )}
                </span>
              </div>
            </div>
            <button
              className={styles.reminderEditLink}
              onClick={() => {
                setEditingReminder(lastSavedReminder)
                setShowCustomReminder(true)
              }}
            >
              Edit
            </button>
          </div>
        )}
      </div>

      <footer className={styles.footer}>
        <Button
          variant="outline"
          fullWidth
          onClick={() => navigate(`/activity/${activityId}`)}
        >
          Try Again
        </Button>
        <Button
          variant="primary"
          fullWidth
          onClick={handleDone}
        >
          Done
        </Button>
      </footer>

      {/* Reminder Modal - Shows existing reminders */}
      {showReminderModal && (
        <div className={styles.modalOverlay} onClick={() => setShowReminderModal(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <ReminderModal
              reminders={reminders}
              onAddNew={() => {
                setShowReminderModal(false)
                setShowReminderPicker(true)
              }}
              onEdit={(reminder) => {
                setEditingReminder(reminder)
                setShowReminderModal(false)
                setShowCustomReminder(true)
              }}
              onDelete={handleReminderDelete}
              onClose={() => setShowReminderModal(false)}
            />
          </div>
        </div>
      )}

      {/* Reminder Picker Modal */}
      {showReminderPicker && (
        <div className={styles.modalOverlay} onClick={() => setShowReminderPicker(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <ReminderPicker
              onSave={handleReminderSave}
              onClose={() => setShowReminderPicker(false)}
              onCustomClick={() => {
                setShowReminderPicker(false)
                setEditingReminder(null)
                setShowCustomReminder(true)
              }}
            />
          </div>
        </div>
      )}

      {/* Custom Reminder Modal */}
      {showCustomReminder && (
        <div className={styles.modalOverlay} onClick={() => {
          setShowCustomReminder(false)
          setEditingReminder(null)
        }}>
          <div onClick={(e) => e.stopPropagation()}>
            <CustomReminderModal
              reminder={editingReminder}
              onSave={editingReminder
                ? (date, config) => handleReminderUpdate(editingReminder.id, date, config)
                : handleReminderSave
              }
              onClose={() => {
                setShowCustomReminder(false)
                setEditingReminder(null)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
